import React, { useEffect, useState } from "react";
import {
  Users,
  ChalkboardSimple,
  ArrowRight,
  ChalkboardTeacher,
  Receipt,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function TeacherDashboard() {
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [classes, setClasses] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [todayMine, setTodayMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, c, a, t] = await Promise.all([
          api.get("/school/me"),
          api.get("/school/teacher/me/classes"),
          api.get("/school/sessions/active"),
          api.get("/school/operations/me/today").catch(() => ({ data: null })),
        ]);
        setSchool(s.data);
        setClasses(c.data);
        setActiveSession(a.data);
        setTodayMine(t.data);
      } catch (e) {
        setError(formatApiError(e));
      }
      setLoading(false);
    })();
  }, []);

  const totalStudents = classes.reduce((sum, c) => sum + (c.student_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="teacher-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          {school && (
            <div
              className="h-14 w-14 rounded-md flex items-center justify-center text-white text-xl font-bold font-heading"
              style={{ backgroundColor: school.theme_color }}
            >
              {school.school_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Teacher</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
              Hello, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {activeSession ? (
                <>Session <strong className="text-slate-900">{activeSession.name}</strong> · {school?.school_name}</>
              ) : (
                <>{school?.school_name || "Your workspace"}</>
              )}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="stat-my-classes" className="p-5 border border-slate-200 bg-white rounded-lg">
          <div className="h-9 w-9 rounded-md bg-blue-100 text-blue-900 flex items-center justify-center">
            <ChalkboardSimple size={18} weight="duotone" />
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">My classes</div>
            <div className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">{classes.length}</div>
          </div>
        </div>
        <div data-testid="stat-my-students" className="p-5 border border-slate-200 bg-white rounded-lg">
          <div className="h-9 w-9 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Users size={18} weight="duotone" />
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Students under my care</div>
            <div className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">{totalStudents}</div>
          </div>
        </div>
        <div data-testid="stat-today-receipts" className="p-5 border border-slate-200 bg-white rounded-lg">
          <div className="h-9 w-9 rounded-md bg-violet-100 text-violet-700 flex items-center justify-center">
            <Receipt size={18} weight="duotone" />
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Today's receipts</div>
            <div className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
              {todayMine?.today?.receipts ?? 0}
            </div>
          </div>
        </div>
        <div className="p-5 border border-slate-200 bg-white rounded-lg">
          <div className="h-9 w-9 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center">
            <Receipt size={18} weight="duotone" />
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Fee collection</div>
            <Link
              to="/collect"
              data-testid="dashboard-collect-link"
              className="inline-flex items-center gap-1 text-base text-blue-900 font-semibold mt-1 hover:underline"
            >
              Open workspace <ArrowRight size={14} />
            </Link>
            <div className="text-xs text-slate-500 mt-0.5">Search students · collect · share receipt</div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-medium text-slate-900 font-heading mb-3">My classes</h3>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : classes.length === 0 ? (
          <div className="p-12 border border-dashed border-slate-200 bg-white rounded-lg text-center" data-testid="teacher-no-classes">
            <ChalkboardSimple size={32} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">
              You haven't been assigned to any classes yet. Ask your school admin to add you.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teacher-classes-grid">
            {classes.map((c) => (
              <div
                key={c.id}
                className="p-5 border border-slate-200 bg-white rounded-lg hover:border-slate-300 transition-colors"
                data-testid={`teacher-class-card-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-500">{c.session_name}</div>
                    <div className="text-lg font-bold text-slate-900 font-heading mt-0.5 truncate">
                      {c.name}{c.section ? ` - ${c.section}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-bold text-slate-900 font-heading">
                      {c.student_count}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Students</div>
                  </div>
                </div>
                {c.description && <p className="text-xs text-slate-500 mt-2">{c.description}</p>}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Fee collection unlocks in next phase</span>
                  <ArrowRight size={14} className="text-slate-300" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
